import {
  Typography,
  ListItemAvatar,
  Avatar,
  AvatarGroup,
  ListItem as MUIListItem,
  Box,
} from '@mui/material';
import SvgIconStyle from '@components/SvgIconStyle';
import { FC } from 'react';
import { useStyles } from './styles';
import { ListItem as ListItemType } from '../../types';
import { DialogListJSONAPI } from '~/api/resources/dialog_list';
import useLocales from '~/hooks/useLocales';

type Props = {
  list: DialogListJSONAPI;
  active: string | undefined;
  setActive: (id: string, listId: string) => void;
};

const ListItem: FC<Props> = ({ list, active, setActive }) => {
  const { t } = useLocales();

  const item: ListItemType = {
    id: list.id,
    shortName: list.attributes.name.match(/\b(\w)/g)?.join('') || '',
    name: list.attributes.name,
    color: list.attributes.color,
  };

  const styles = useStyles({ isActive: active === item.id, color: item.color });

  const sharedUserIds =
    list.relationships?.shared_users?.data?.map((u) => u.id) ||
    (list.attributes.shared_user_ids || []).map(String);
  const rawSharedUsers = (list.relationships?.shared_users?.data || []) as {
    id: string;
    attributes?: { first_name: string; last_name: string; avatar_url?: string };
  }[];
  const sharedUsers = rawSharedUsers.filter((u) => sharedUserIds.includes(u.id));

  const isShared = list.attributes.visibility
    ? list.attributes.visibility === 'shared'
    : Boolean(list.attributes.public || sharedUserIds.length > 0);
  const isSharedSpecific = list.attributes.shared_scope
    ? list.attributes.shared_scope === 'specific'
    : sharedUserIds.length > 0 && !list.attributes.public;

  return (
    <MUIListItem
      data-role={`add-to-list-popup-list-${list.id}`}
      onClick={() => setActive(item.id, list.id)}
      key={item.id}
      sx={styles.listItem}
    >
      <ListItemAvatar>
        <Avatar sx={styles.avatar}>
          <Typography variant="body2" sx={styles.avatarText}>
            {item.shortName}
          </Typography>
        </Avatar>
      </ListItemAvatar>
      <Typography variant="body2">{item.name}</Typography>
      <Box
        sx={{
          ml: 1,
          mr: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'divider',
        }}
      >
        {isShared ? (
          <>
            <SvgIconStyle
              src="/icons/share/24/outline.svg"
              sx={{ width: 18, height: 18, color: 'text.secondary' }}
            />
            {isSharedSpecific && sharedUsers.length > 0 ? (
              <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
                {sharedUsers.map((user) => (
                  <Avatar
                    key={user.id}
                    src={user.attributes?.avatar_url}
                    alt={`${user.attributes?.first_name ?? ''} ${user.attributes?.last_name ?? ''}`}
                  >
                    {`${user.attributes?.first_name?.[0] || ''}${
                      user.attributes?.last_name?.[0] || ''
                    }`}
                  </Avatar>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.search.results.create_edit_list.shared_all')}
              </Typography>
            )}
          </>
        ) : (
          <>
            <SvgIconStyle
              src="/icons/lock/24/outline.svg"
              sx={{ width: 18, height: 18, color: 'text.secondary' }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.search.results.create_edit_list.private_only_me')}
            </Typography>
          </>
        )}
      </Box>
      {active === item.id && (
        <SvgIconStyle src="/icons/check/24/solid.svg" sx={styles.activeIcon} />
      )}
    </MUIListItem>
  );
};

export default ListItem;
